import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './i18n';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';
// Eager-import Button.css so the .btn-* rules ship in the main entry
// chunk (not duplicated across the 6 route chunks that import Button).
// Without this, webpack's CSS code-splitting put a copy of the rules
// in each route's chunk — ~6 KiB of duplication across the bundle.
// Loading it from index.js means the rules are part of main.css and
// available on first paint to any component that uses .btn-* classes
// (including any future static prerender output).
import './components/Button.css';

// Side-effect imports — both modules self-initialise on import.
//   • lib/sentry: starts the dynamic import of @sentry/browser if
//     REACT_APP_SENTRY_DSN is set; no-op otherwise.
//   • reportWebVitals: subscribes to CWV measurements and forwards them
//     to GA4 (consent-gated) and Sentry. Subscription is set up
//     immediately so we don't miss the first paint.
// Importing here, before the React render call, ensures the
// instrumentation is in place before any user-facing code runs.
import './lib/sentry';
import './reportWebVitals';
import { warmupAll } from './lib/lazyWithRetry';

// Mount strategy:
//   • If the HTML was prerendered at build time (scripts/prerender.mjs
//     tags <body data-prerendered="true">), use hydrateRoot — React
//     reuses the existing DOM, attaches event handlers in place, and
//     does NOT re-create the LCP-relevant elements. The mobile-LCP
//     metric fires on the prerendered paint (~0.5–1 s on simulated
//     mobile) instead of waiting for React's mount (~3 s).
//   • Otherwise (preview deploys without prerender, dynamic routes,
//     local dev), use createRoot — same fresh-render behaviour we've
//     always had.
//
// React 19 hydration is forgiving about minor mismatches: it logs a
// warning and re-renders the offending subtree rather than bailing.
// That means most localStorage / navigator.language / Date-based
// runtime variation in components doesn't break hydration, it just
// triggers an extra render. We still want zero warnings in production
// for clean DevTools — chase any that appear by deferring the
// browser-only state to useEffect.
const rootEl = document.getElementById('root');
const tree = (
  <HelmetProvider>
    <BrowserRouter>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </BrowserRouter>
  </HelmetProvider>
);

if (document.body.dataset.prerendered === 'true') {
  // Pre-warm every React.lazy() chunk before hydrating. Without this,
  // every Suspense boundary (route, CookieBanner, StickyApply,
  // GoogleAnalytics) throws a Promise on first render, Suspense
  // substitutes the fallback (null in our case), the static-skeleton
  // DOM is thrown away → React error #418 → re-render from scratch,
  // paying for both the static paint AND the React-mount paint. This
  // is what scripts/prerender.mjs's "PRERENDER is opt-in" comment
  // warns about. warmupAll() resolves once every memoised factory has
  // settled, so by the time hydrateRoot fires every lazy component
  // can render synchronously.
  warmupAll().then(() => ReactDOM.hydrateRoot(rootEl, tree));
} else {
  ReactDOM.createRoot(rootEl).render(tree);
}
